import './Player.css';
import {useEffect, useRef} from 'react';
import * as Sentry from '@sentry/react';

// Adapter: wraps JW Player instance to expose a Video.js-compatible API
// so that consumers (Episode.jsx, Movie.jsx, etc.) don't need changes.
const createPlayerAdapter = (jwInstance) => {
    let pendingSeek = null;

    // When seek is called before media is loaded, queue it for the first 'time' event
    jwInstance.once('time', () => {
        if (pendingSeek !== null) {
            jwInstance.seek(pendingSeek);
            pendingSeek = null;
        }
    });

    return {
        // Time — if called before media loads, queue the seek
        currentTime: (val) => {
            if (val !== undefined) {
                const state = jwInstance.getState();
                if (state === 'idle' || state === 'buffering') {
                    pendingSeek = val;
                } else {
                    jwInstance.seek(val);
                }
                return;
            }
            return jwInstance.getPosition();
        },
        duration: () => jwInstance.getDuration(),

        // Events — map Video.js names to JW Player names
        on: (event, cb) => {
            const eventMap = {
                timeupdate: 'time',
                ended: 'complete',
            };
            jwInstance.on(eventMap[event] || event, cb);
        },
        off: (event, cb) => {
            const eventMap = {
                timeupdate: 'time',
                ended: 'complete',
            };
            jwInstance.off(eventMap[event] || event, cb);
        },

        // Playback
        play: () => jwInstance.play(),
        pause: () => jwInstance.pause(),

        // Source
        src: (sources) => {
            const src = Array.isArray(sources) ? sources[0]?.src : sources?.src;
            if (src) jwInstance.load([{file: src}]);
        },
        currentSrc: () => {
            const item = jwInstance.getPlaylistItem();
            return item?.file || item?.sources?.[0]?.file || '';
        },

        // Poster
        poster: (url) => {
            if (url) {
                const item = jwInstance.getPlaylistItem();
                if (item) item.image = url;
            }
        },

        // State
        isFullscreen: () => jwInstance.getFullscreen(),
        error: () => null,
        el: () => jwInstance.getContainer(),

        // Cleanup
        remove: () => jwInstance.remove(),

        // Big play button — JW Player handles this natively, no-op
        bigPlayButton: {hide: () => {}, show: () => {}},
    };
};

const Player = ({options, onReady, style}) => {
    const containerRef = useRef(null);
    const playerRef = useRef(null);
    const initializedRef = useRef(false);

    // Initialize player once on mount
    useEffect(() => {
        if (!containerRef.current || typeof window.jwplayer !== 'function') return;

        const src = Array.isArray(options.sources)
            ? options.sources[0]?.src
            : options.sources?.src;

        const jwInstance = window.jwplayer(containerRef.current).setup({
            file: src,
            image: options.poster || '',
            autostart: options.autoplay || false,
            controls: options.controls !== false,
            preload: 'auto',
            stretching: 'uniform',
            width: '100%',
            aspectratio: '16:9',
            cast: {},
            playbackRateControls: true,
            rewind: false,
            skin: {
                name: 'turkish4love',
            },
        });

        // Ensure Cast framework is initialized with the default media receiver
        const initCast = () => {
            if (!window.cast?.framework || !window.chrome?.cast) return false;
            const castContext = window.cast.framework.CastContext.getInstance();
            castContext.setOptions({
                receiverApplicationId: window.chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
                autoJoinPolicy: window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
            });
            return true;
        };
        if (!initCast()) {
            // Cast SDK not yet loaded by JWPlayer — wait for it
            window['__onGCastApiAvailable'] = (isAvailable) => {
                if (isAvailable) initCast();
            };
        }

        const adapter = createPlayerAdapter(jwInstance);
        playerRef.current = adapter;
        initializedRef.current = true;

        // Notify consumer once player is ready (seek will work after this)
        jwInstance.on('ready', () => {
            onReady(playerRef);
        });

        // Inject skip buttons into .jw-button-container after the play button.
        // Hide the built-in rewind and replace with custom rewind + forward.
        const injectSkipButtons = () => {
            const container = jwInstance.getContainer();
            if (!container) return false;

            const buttonContainer = container.querySelector('.jw-button-container');
            const playBtn = buttonContainer?.querySelector('.jw-icon-playback');
            if (!buttonContainer || !playBtn) return false;

            // Avoid double-injection
            if (container.querySelector('.jw-skip-back')) return true;

            // Hide the built-in rewind button
            const builtInRewind = buttonContainer.querySelector('.jw-icon-rewind');
            if (builtInRewind) builtInRewind.style.display = 'none';

            const createSkipBtn = (className, label, svgPath, onClick) => {
                const btn = document.createElement('div');
                btn.className = `jw-icon jw-icon-inline jw-button-color jw-reset ${className}`;
                btn.setAttribute('role', 'button');
                btn.setAttribute('tabindex', '0');
                btn.setAttribute('aria-label', label);
                btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="${svgPath}"/><text x="12" y="15" text-anchor="middle" font-size="6" font-weight="bold" fill="currentColor">10</text></svg>`;
                btn.addEventListener('click', onClick);
                return btn;
            };

            const rewindBtn = createSkipBtn(
                'jw-skip-back',
                'Rewind 10s',
                'M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z',
                () => { jwInstance.seek(Math.max(0, jwInstance.getPosition() - 10)); },
            );

            const forwardBtn = createSkipBtn(
                'jw-skip-forward',
                'Forward 10s',
                'M12 5V1l5 5-5 5V7c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z',
                () => { jwInstance.seek(Math.min(jwInstance.getDuration(), jwInstance.getPosition() + 10)); },
            );

            // Insert after the play button
            buttonContainer.insertBefore(rewindBtn, playBtn.nextSibling);
            buttonContainer.insertBefore(forwardBtn, rewindBtn.nextSibling);
            return true;
        };

        // Try on multiple events to ensure buttons are injected
        injectSkipButtons();
        jwInstance.on('ready', () => { injectSkipButtons(); });
        jwInstance.on('playlistItem', () => { injectSkipButtons(); });
        jwInstance.on('firstFrame', () => { injectSkipButtons(); });
        // Fallback: poll briefly in case JW Player rebuilds the DOM async
        let attempts = 0;
        const interval = setInterval(() => {
            if (injectSkipButtons() || ++attempts > 20) clearInterval(interval);
        }, 250);

        // Error handling + Sentry
        jwInstance.on('error', (e) => {
            console.error('[Player] JW Player error:', e);
            Sentry.captureException(new Error(`JW Player error: ${e.message}`), {
                tags: {component: 'Player', errorCode: e.code},
                extra: {sourceFile: src},
            });
        });

        jwInstance.on('setupError', (e) => {
            console.error('[Player] JW Player setup error:', e);
            Sentry.captureException(new Error(`JW Player setup error: ${e.message}`), {
                tags: {component: 'Player', errorCode: e.code},
            });
        });

        // Lock to landscape when entering fullscreen on mobile
        jwInstance.on('fullscreen', ({fullscreen}) => {
            const el = jwInstance.getContainer();

            if (fullscreen) {
                screen.orientation?.lock?.('landscape').catch(() => {
                    const checkPortrait = () => {
                        if (window.innerHeight > window.innerWidth) {
                            el.classList.add('jw-force-landscape');
                        }
                    };
                    checkPortrait();
                    el._portraitCheck = () => {
                        if (!jwInstance.getFullscreen()) return;
                        if (window.innerHeight > window.innerWidth) {
                            el.classList.add('jw-force-landscape');
                        } else {
                            el.classList.remove('jw-force-landscape');
                        }
                    };
                    window.addEventListener('resize', el._portraitCheck);
                });
            } else {
                screen.orientation?.unlock?.();
                el.classList.remove('jw-force-landscape');
                if (el._portraitCheck) {
                    window.removeEventListener('resize', el._portraitCheck);
                    el._portraitCheck = null;
                }
            }
        });

        return () => {
            try { jwInstance.remove(); } catch (_) {}
            playerRef.current = null;
            initializedRef.current = false;
        };
    }, []);

    // Update source/poster on option changes (not initial mount)
    useEffect(() => {
        if (!initializedRef.current || !playerRef.current) return;

        const newSrc = Array.isArray(options.sources)
            ? options.sources[0]?.src
            : options.sources?.src;
        const currentSrc = playerRef.current.currentSrc();

        if (newSrc && newSrc !== currentSrc) {
            playerRef.current.poster(options.poster);
            playerRef.current.src(options.sources);
            if (options.autoplay) playerRef.current.play();
        }
    }, [options]);

    return (
        <div className='aspect-video w-full rounded-lg overflow-hidden'>
            <div ref={containerRef} style={style} />
        </div>
    );
};

export default Player;
